import { useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckSquare,
  ClipboardCheck,
  Compass,
  Gauge,
  GraduationCap,
  Megaphone,
  Mountain,
  Sparkles,
  Users,
} from 'lucide-react';

import { getMyDay, type MyDayPayload } from '@/api/eos/myDay.api';
import { completeTodo } from '@/api/eos/todos.api';
import { getVto, normalizeVtoData } from '@/api/eos/vto.api';
import { getMyTraining, type MyTrainingPayload } from '@/api/training.api';
import { formatSopIdentifier } from '@/api/sops.api';
import { RockStatusBadge } from '@/components/eos/RockStatusBadge';
import { DailyMetricRow } from '@/components/scorecards/DailyMetricRow';
import { DailyProgressRing } from '@/components/scorecards/DailyProgressRing';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { describeEosError } from '@/lib/eos/errors';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import type { MetricStatus } from '@/lib/scorecards/status';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * My Day — the personal dashboard (Stream B Wave 4, ADR-023). One
 * composite read (`/api/eos/my-day`) feeds a grid of cards: my to-dos,
 * my rocks, my metrics, upcoming L10s, queued headlines, and (for
 * leads) the team pulse. The Training and SOP-acknowledgment cards ride
 * a second composite read (`/api/training/me`, Stream C).
 */

const METRIC_STATUS_TONE: Record<MetricStatus, string> = {
  green: 'bg-kw-emerald-light text-kw-tag-text',
  yellow: 'bg-kw-warning-bg text-kw-warning-text',
  red: 'bg-kw-danger-bg text-kw-danger-text',
};

const TODO_PREVIEW_COUNT = 5;

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function meetingTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function MyDayPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = state.status === 'authenticated' ? state.me : null;
  const firstName = me?.user.firstName ?? null;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const myDayQuery = useQuery({ queryKey: ['eos', 'my-day'], queryFn: getMyDay });
  const trainingQuery = useQuery({ queryKey: ['training', 'me'], queryFn: getMyTraining });
  // Core values ride the shared V/TO query (PA-page pattern) — no server
  // change: single-storage rule. 5-min stale window; values change rarely.
  const vtoQuery = useQuery({
    queryKey: ['eos', 'vto'],
    queryFn: () => getVto(),
    staleTime: 5 * 60 * 1000,
  });
  const coreValues = useMemo(
    () => normalizeVtoData(vtoQuery.data?.current?.dataJson).coreValues.filter((v) => v.trim() !== ''),
    [vtoQuery.data],
  );
  const canManageEos = me !== null && (isSuperadmin(me) || hasCapability(me, 'manage_eos'));

  const completeMutation = useMutation({
    mutationFn: (todoId: string) => completeTodo(todoId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['eos', 'my-day'] });
      void queryClient.invalidateQueries({ queryKey: ['eos', 'todos'] });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not complete the to-do.'), { variant: 'danger' });
    },
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="typo-h1">
          {greeting}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="mt-1 text-[14px] text-kw-muted">Here’s your day at a glance.</p>
      </div>

      {myDayQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : myDayQuery.error || !myDayQuery.data ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load your day</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {myDayQuery.error instanceof Error ? myDayQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : (
        <DayGrid
          payload={myDayQuery.data}
          training={trainingQuery.data ?? null}
          coreValues={coreValues}
          canManageEos={canManageEos}
          completePending={completeMutation.isPending}
          onCompleteTodo={(todoId) => {
            completeMutation.mutate(todoId);
          }}
          onJoinMeeting={(meetingId) => {
            void navigate(`/eos/meetings/${meetingId}/run`);
          }}
        />
      )}
    </div>
  );
}

function DayGrid({
  payload,
  training,
  coreValues,
  canManageEos,
  completePending,
  onCompleteTodo,
  onJoinMeeting,
}: {
  payload: MyDayPayload;
  training: MyTrainingPayload | null;
  coreValues: string[];
  canManageEos: boolean;
  completePending: boolean;
  onCompleteTodo: (todoId: string) => void;
  onJoinMeeting: (meetingId: string) => void;
}) {
  const nowMs = Date.now();
  const todos = payload.myTodos.items.slice(0, TODO_PREVIEW_COUNT);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {/* My To-Dos */}
      <SectionCard
        icon={<CheckSquare className="size-4 text-kw-cerulean" />}
        title="My To-Dos"
        titleExtra={
          payload.myTodos.overdueCount > 0 ? (
            <Badge variant="danger">{payload.myTodos.overdueCount} overdue</Badge>
          ) : null
        }
        action={<CardLink to="/eos/todos" label="All to-dos" />}
      >
        {todos.length === 0 ? (
          <EmptyHint text="Nothing on your list." to="/eos/todos" cta="Add a to-do" />
        ) : (
          <ul className="space-y-2">
            {todos.map((t) => {
              const overdue = Date.parse(t.dueDate) < nowMs;
              return (
                <li key={t.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    aria-label={`Complete ${t.title}`}
                    checked={t.completedAt !== null}
                    disabled={completePending}
                    onChange={() => {
                      onCompleteTodo(t.id);
                    }}
                    className="size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-kw-shark">{t.title}</span>
                  <span
                    className={`shrink-0 text-[11px] ${
                      overdue ? 'font-medium text-kw-danger-text' : 'text-kw-muted'
                    }`}
                  >
                    {shortDate(t.dueDate)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {/* My Rocks */}
      <SectionCard
        icon={<Mountain className="size-4 text-kw-cerulean" />}
        title="My Rocks"
        action={<CardLink to="/eos/rocks" label="All rocks" />}
      >
        {payload.myRocks.length === 0 ? (
          <EmptyHint text="No rocks this quarter." to="/eos/rocks" cta="Set a rock" />
        ) : (
          <ul className="space-y-2.5">
            {payload.myRocks.map((rock) => (
              <li key={rock.id} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] text-kw-shark">{rock.title}</p>
                  <p className="text-[11px] text-kw-muted">
                    {rock.milestonesDone}/{rock.milestonesTotal} milestones
                  </p>
                </div>
                <RockStatusBadge status={rock.status} />
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* My Scorecard */}
      <SectionCard
        icon={<BarChart3 className="size-4 text-kw-cerulean" />}
        title="My Scorecard"
        action={<CardLink to="/scorecards" label="Scorecards" />}
      >
        {payload.myMetrics.length === 0 ? (
          <EmptyHint text="You don’t own any metrics." to="/scorecards" cta="View scorecards" />
        ) : (
          <ul className="space-y-2.5">
            {payload.myMetrics.map((metric) =>
              metric.daily ? (
                // ADR-048: a daily goal gets today's ring, the streak and "log today".
                <DailyMetricRow key={metric.id} metric={{ ...metric, daily: metric.daily }} />
              ) : (
                <li key={metric.id} className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] text-kw-shark">{metric.title}</p>
                    <p className="text-[11px] text-kw-muted">Goal {metric.goal}</p>
                  </div>
                  {metric.latestEntry ? (
                    <span
                      className={`rounded px-2 py-0.5 text-[12px] font-medium ${
                        METRIC_STATUS_TONE[metric.latestEntry.status]
                      }`}
                    >
                      {metric.latestEntry.value}
                    </span>
                  ) : (
                    <span className="text-[12px] text-kw-ghost">—</span>
                  )}
                </li>
              ),
            )}
          </ul>
        )}
      </SectionCard>

      {/* Department pace today (ADR-048) — the department's numbers, no people, no ranking. */}
      {payload.departmentPace ? (
        <SectionCard
          icon={<Gauge className="size-4 text-kw-cerulean" />}
          title={`${payload.departmentPace.departmentName} pace today`}
          action={<CardLink to="/scorecards" label="Scorecards" />}
        >
          <ul className="space-y-2.5">
            {payload.departmentPace.metrics.map((m) => (
              <li key={m.id} className="flex items-center gap-3">
                <DailyProgressRing progress={m.progress} size={36} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] text-kw-shark">{m.title}</p>
                  <p className="text-[11px] text-kw-muted">
                    {m.progress.display === 'attainment'
                      ? `${String(m.progress.value)} of ${String(m.progress.goal)}${m.unitLabel ? ` ${m.unitLabel}` : ''}`
                      : m.progress.display === 'budget'
                        ? `${String(m.progress.value)} of a ${String(m.progress.goal)} budget`
                        : m.progress.display === 'level'
                          ? `Goal ${String(m.progress.goal)}`
                          : 'Nothing logged yet today'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      {/* Upcoming L10s */}
      <SectionCard
        icon={<CalendarDays className="size-4 text-kw-cerulean" />}
        title="Upcoming L10s"
        action={<CardLink to="/eos/meetings" label="Meetings" />}
      >
        {payload.upcomingMeetings.length === 0 ? (
          <EmptyHint text="No upcoming L10s." to="/eos/meetings" cta="View meetings" />
        ) : (
          <ul className="space-y-2.5">
            {payload.upcomingMeetings.map((m) => (
              <li key={m.id} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] text-kw-shark">{m.seriesName ?? 'L10 meeting'}</p>
                  <p className="text-[11px] text-kw-muted">{meetingTime(m.scheduledFor)}</p>
                </div>
                {m.status === 'in_progress' ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      onJoinMeeting(m.id);
                    }}
                  >
                    Join
                  </Button>
                ) : (
                  <Link
                    to="/eos/meetings"
                    className="text-[12px] font-medium text-kw-cerulean hover:underline"
                  >
                    Open
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* Headlines queued */}
      <SectionCard
        icon={<Megaphone className="size-4 text-kw-cerulean" />}
        title="Headlines queued"
        action={<CardLink to="/eos/meetings" label="Meetings" />}
      >
        {payload.myHeadlinesQueued > 0 ? (
          <p className="text-[13px] text-kw-shark">
            <span className="text-[22px] font-semibold">{payload.myHeadlinesQueued}</span>{' '}
            <span className="text-kw-muted">
              headline{payload.myHeadlinesQueued === 1 ? '' : 's'} waiting for your next L10.
            </span>
          </p>
        ) : (
          <p className="text-[13px] text-kw-muted">No headlines queued — share good news at your next L10.</p>
        )}
      </SectionCard>

      {/* Team pulse — leads only (payload decides) */}
      {payload.teamPulse ? (
        <SectionCard icon={<Users className="size-4 text-kw-cerulean" />} title="Team pulse">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-kw-md bg-kw-bg p-2.5">
              <p className="text-[18px] font-semibold text-kw-shark">{payload.teamPulse.openIssuesCount}</p>
              <p className="mt-0.5 text-[11px] text-kw-muted">Open issues</p>
            </div>
            <div className="rounded-kw-md bg-kw-bg p-2.5">
              <p className="text-[18px] font-semibold text-kw-shark">
                {payload.teamPulse.offTrackRocksCount}
              </p>
              <p className="mt-0.5 text-[11px] text-kw-muted">Rocks off track</p>
            </div>
            <div className="rounded-kw-md bg-kw-bg p-2.5">
              <p className="text-[18px] font-semibold text-kw-shark">
                {payload.teamPulse.teamOverdueTodosCount}
              </p>
              <p className="mt-0.5 text-[11px] text-kw-muted">Overdue to-dos</p>
            </div>
          </div>
        </SectionCard>
      ) : null}

      {/* Announcements — unread + pending acks (Stream E / ADR-027). */}
      <SectionCard
        icon={<Megaphone className="size-4 text-kw-cerulean" />}
        title="Announcements"
        titleExtra={
          payload.comms.unreadAnnouncements.items.some((a) => a.requiresAck) ? (
            <Badge variant="danger">Action needed</Badge>
          ) : payload.comms.unreadAnnouncements.count > 0 ? (
            <Badge variant="info">{payload.comms.unreadAnnouncements.count} unread</Badge>
          ) : null
        }
        action={<CardLink to="/communication" label="Open" />}
      >
        {payload.comms.unreadAnnouncements.items.length === 0 ? (
          <p className="text-[13px] text-kw-muted">You’re all caught up on announcements.</p>
        ) : (
          <ul className="space-y-2.5">
            {payload.comms.unreadAnnouncements.items.map((a) => (
              <li key={a.id}>
                <Link to="/communication" className="block">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-kw-shark hover:underline">
                      {a.title}
                    </span>
                    {a.requiresAck ? <Badge variant="danger">Ack</Badge> : null}
                  </div>
                  <p className="text-[11px] text-kw-muted">
                    {a.authorNameSnapshot} · {shortDate(a.publishedAt)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* Shout-outs — recent peer recognition (Stream E / ADR-027). */}
      <SectionCard
        icon={<Sparkles className="size-4 text-kw-cerulean" />}
        title="Shout-outs"
        action={<CardLink to="/communication?tab=shoutouts" label="All" />}
      >
        {payload.comms.recentShoutOuts.items.length === 0 ? (
          <EmptyHint
            text="No recent shout-outs."
            to="/communication?compose=shoutout"
            cta="Give a shout-out"
          />
        ) : (
          <div className="space-y-2.5">
            <ul className="space-y-2.5">
              {payload.comms.recentShoutOuts.items.map((s) => (
                <li key={s.id}>
                  <p className="text-[13px] text-kw-shark">
                    <span className="font-medium">{s.recipientNameSnapshot}</span>
                    <span className="text-kw-muted"> · {s.coreValue}</span>
                  </p>
                  <p className="truncate text-[12px] text-kw-muted">{s.message}</p>
                </li>
              ))}
            </ul>
            <Link
              to="/communication?compose=shoutout"
              className="inline-block text-[12px] font-medium text-kw-cerulean hover:underline"
            >
              Give a shout-out
            </Link>
          </div>
        )}
      </SectionCard>

      {/* Our Core Values — from the shared V/TO query (Stream E / ADR-027). */}
      <SectionCard
        icon={<Compass className="size-4 text-kw-cerulean" />}
        title="Our Core Values"
        action={canManageEos ? <CardLink to="/eos/vto" label="Edit" /> : undefined}
      >
        {coreValues.length === 0 ? (
          canManageEos ? (
            <EmptyHint text="No core values defined yet." to="/eos/vto" cta="Define them" />
          ) : (
            <p className="text-[13px] text-kw-muted">Your leadership hasn’t defined core values yet.</p>
          )
        ) : (
          <ol className="list-decimal space-y-1.5 pl-5">
            {coreValues.map((value) => (
              <li key={value} className="text-[13px] text-kw-shark">
                {value}
              </li>
            ))}
          </ol>
        )}
      </SectionCard>

      {/* Training + SOP acknowledgments — fed by /api/training/me (Stream C). */}
      <TrainingCard training={training} />
      <SopAcksCard training={training} />
    </div>
  );
}

const TRAINING_PREVIEW_COUNT = 3;

function TrainingCard({ training }: { training: MyTrainingPayload | null }) {
  const active = (training?.enrollments ?? []).filter((e) => e.completedAt === null);
  const overdueCount = active.filter((e) => e.isOverdue).length;
  const preview = active.slice(0, TRAINING_PREVIEW_COUNT);

  return (
    <SectionCard
      icon={<GraduationCap className="size-4 text-kw-cerulean" />}
      title="Training"
      titleExtra={overdueCount > 0 ? <Badge variant="danger">{overdueCount} overdue</Badge> : null}
      action={<CardLink to="/training" label="My training" />}
    >
      {training === null ? (
        <p className="text-[13px] text-kw-muted">Couldn&apos;t load training.</p>
      ) : preview.length === 0 ? (
        <EmptyHint text="No training assigned." to="/training" cta="View training" />
      ) : (
        <ul className="space-y-2.5">
          {preview.map((e) => (
            <li key={e.id} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <Link
                  to={`/training/courses/${e.courseId}`}
                  className="block truncate text-[13px] text-kw-shark hover:underline"
                >
                  {e.course.title}
                </Link>
                <p className="text-[11px] text-kw-muted">
                  {e.progress.completedLessons}/{e.progress.totalLessons} lessons
                </p>
              </div>
              {e.dueAt ? (
                <span
                  className={`shrink-0 text-[11px] ${
                    e.isOverdue ? 'font-medium text-kw-danger-text' : 'text-kw-muted'
                  }`}
                >
                  {shortDate(e.dueAt)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

const ACK_PREVIEW_COUNT = 3;

function SopAcksCard({ training }: { training: MyTrainingPayload | null }) {
  const pending = training?.pendingSopAcks ?? [];
  const preview = pending.slice(0, ACK_PREVIEW_COUNT);

  return (
    <SectionCard
      icon={<ClipboardCheck className="size-4 text-kw-cerulean" />}
      title="SOP acknowledgments"
      titleExtra={pending.length > 0 ? <Badge variant="warning">{pending.length} pending</Badge> : null}
      action={<CardLink to="/training" label="My training" />}
    >
      {training === null ? (
        <p className="text-[13px] text-kw-muted">Couldn&apos;t load acknowledgments.</p>
      ) : pending.length === 0 ? (
        <p className="text-[13px] text-kw-muted">All caught up — nothing waiting for your signature.</p>
      ) : (
        <ul className="space-y-2.5">
          {preview.map((ack) => (
            <li key={ack.sopVersionId}>
              <Link
                to={`/compliance/sops/${ack.slug}`}
                className="block truncate text-[13px] text-kw-shark hover:underline"
              >
                <span className="font-mono text-[11px] text-kw-muted">{formatSopIdentifier(ack)}</span>{' '}
                {ack.title}
              </Link>
            </li>
          ))}
          {pending.length > preview.length ? (
            <li className="text-[11px] text-kw-muted">
              + {pending.length - preview.length} more to acknowledge
            </li>
          ) : null}
        </ul>
      )}
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Small building blocks
// ─────────────────────────────────────────────────────────────────────────

function SectionCard({
  icon,
  title,
  titleExtra,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  titleExtra?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="flex flex-col p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {icon}
          <h2 className="truncate text-[14px] font-semibold text-kw-shark">{title}</h2>
          {titleExtra}
        </div>
        {action}
      </div>
      <div className="flex-1">{children}</div>
    </Card>
  );
}

function CardLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="shrink-0 text-[12px] font-medium text-kw-cerulean hover:underline">
      {label}
    </Link>
  );
}

function EmptyHint({ text, to, cta }: { text: string; to: string; cta: string }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[13px] text-kw-muted">{text}</p>
      <Link to={to} className="inline-block text-[12px] font-medium text-kw-cerulean hover:underline">
        {cta}
      </Link>
    </div>
  );
}
